import Markdown from 'react-markdown';
import PropTypes from 'prop-types';
import React from 'react';
import { DesignatedFundDonationBox } from './DesignatedFundDonationBox';
import { DesignatedFundLandingActivityFeed } from './DesignatedFundActivityFeed';
import { GenericShareBar } from '../common/GenericShareBar';
import { getCsrfToken } from '../utils/getCsrfToken';
import { LoadingEllipsis } from '../common/LoadingEllipsis';
import { formatCurrencyV2 } from '../common/formatCurrency.ts';
import { Sticky } from '../utils/Sticky';

/* global csrfTokenParameterName */

export let DesignatedFundLanding = function ({
    canonicalUrl,
    title,
    subtitle,
    challengeId,
    designatedFundId,
    photoUrl,
    photoCaption,
    description,
    activityFeedTitle,
    activityFeedDescription,
    activityFeed,
    testimonial,
    donorCount,
    altImageText,
    totalRaised,
    currentGoal,
    acceptingDonations,
}) {
    const [submittingPage, setSubmittingPage] = React.useState(false);
    const alternateDonationTier1Amount = 250;
    const alternateDonationTier2Amount = 500;
    const alternateDonationTier3Amount = 1000;
    const formRef = React.useRef(null);
    const [progressWidth, setProgressWidth] = React.useState(0);

    function resetReactState() {
        setSubmittingPage(false);
    }

    function submitDonation(amount) {
        if (!acceptingDonations) {
            // This shouldn't be possible since the give buttons are disabled/not rendered, but just in case.
            alert('Donations are not being accepted for this fund at this time. Please select a different classroom to support.');
            return;
        }
        if (!amount || amount <= 0) {
            alert('Please enter a valid donation amount.');
            return;
        }
        if (formRef.current) {
            try {
                dcAnalytics.track('Added to Shopping Cart', {
                    'Page Name': pageName,
                    'Add To Cart Price': amount,
                    'Credit To Fund:': challengeId,
                });
            } catch (err) {
                // Do nothing.
            }

            setSubmittingPage(true);
            formRef.current.donationAmount.value = amount;
            formRef.current.submit();
        }
    }

    React.useEffect(() => {
        // Add event listener for pagehide to reset React state
        window.addEventListener('pagehide', resetReactState);
        // Cleanup: (Attempt to) remove event listener when component unmounts
        return () => {
            window.removeEventListener('pagehide', resetReactState);
        };
    }, []);

    React.useEffect(() => {
        const targetWidth = (totalRaised / currentGoal) * 100;

        // requestAnimationFrame executes right before the next repaint.
        const animationFrame = requestAnimationFrame(() => {
            setProgressWidth(targetWidth);
        });

        return () => cancelAnimationFrame(animationFrame);
    }, [totalRaised, currentGoal]);

    return (
        <div className='container campaign-giving-container'>
            <div className='intro'>
                <h1>{title}</h1>
                {subtitle
                    ? <p className='subheader'>
                        <Markdown>{subtitle}</Markdown>
                    </p>
                    : null }
                {acceptingDonations
                    ? <Sticky stickyClassName='fixed'>
                        <div className='give-bar'>
                            <div className='progress-container'>
                                <div className='dollar-amount'>{formatCurrencyV2(totalRaised, 'HIDE_CENTS')} raised <span className='goal'>{formatCurrencyV2(currentGoal, 'HIDE_ZERO_CENTS')} goal</span></div>
                                <div className='progress-bar'>
                                    <div style={{ width: `${progressWidth}%` }} className='progression'></div>
                                </div>
                                <div className='donors'>{donorCount} donor{donorCount > 1 ? 's' : ''}</div>
                            </div>
                            <DesignatedFundDonationBox
                                challengeId={challengeId}
                                submitDonation={submitDonation}
                            />
                            <div>
                                <form className='form-redux progress-bar-form' action={secureBaseURL + 'donors/cart/direct-gift'}
                                    method='post' name='quickDonate' id='quickDonate' ref={formRef}>
                                    <input type='hidden' name={csrfTokenParameterName} value={getCsrfToken()} />
                                    <input type='hidden' name='challengeid' value={challengeId} />
                                    <input type='hidden' name='quickdirectgift' value={'true'} />
                                    <input type='hidden' name='donationAmount' id='donationAmount'/>
                                </form>
                                {submittingPage && <LoadingEllipsis className={'page-loader'} useShield={true} />}
                            </div>
                        </div>
                    </Sticky>
                    : <div className='progress-container not-accepting-donations'>
                        <div className='amount-container'>
                            <div className='dollar-amount'>{formatCurrencyV2(totalRaised, 'HIDE_CENTS')} raised!</div>
                            <div className='donors'>{donorCount} donor{donorCount > 1 ? 's' : ''}</div>
                        </div>
                        <div className="note">Funding has concluded, but you can still impact a <a href={baseURL + 'donors/map.html'}>classroom near you!</a></div>
                    </div> }
                <div className='campaign-image'>
                    <img src={photoUrl + '?auto=webp'} alt={altImageText}/>
                    {photoCaption
                        ? <div className='photo-caption mobile'>
                            <p><Markdown>{photoCaption}</Markdown></p>
                        </div>
                        : null}
                </div>
                {photoCaption
                    ? <div className='photo-caption desktop'>
                        <p><Markdown>{photoCaption}</Markdown></p>
                    </div>
                    : null}
            </div>
            <div className='description'>
                {/* This lives on `challenge`, not `designatedfund`, and it stores HTML for the sake of consistency */}
                {/* with other `challenge` users. It's not clear if that's the right choice. */}
                <p dangerouslySetInnerHTML={{ __html: description }} />
                <h3><Markdown>{activityFeedTitle}</Markdown></h3>
                <p><Markdown>{activityFeedDescription}</Markdown></p>
                {
                    activityFeed != null &&
                    activityFeed.keys &&
                    activityFeed.keys.length > 0
                        ? <DesignatedFundLandingActivityFeed
                            keys={activityFeed.keys}
                            activityFeedComments={activityFeed.activityFeedComments}
                        />
                        : <div className='empty-activity'><div className='now-funding'>Now funding!</div> We&#39;ve just launched this fund and will be updating this space soon with our funding milestones.</div>}
            </div>
            {testimonial
                ? <div className='testimonial'>
                    <div className='testimonial-image' style={{ backgroundImage: `url(${baseS3URL + testimonial.primaryPhotoUrl})` }}>
                        {testimonial.secondaryImageUrl && (
                            <div className='testimonial-teacher' style={{ backgroundImage: `url(${baseS3URL + testimonial.secondaryImageUrl})` }}></div>
                        )}
                    </div>
                    <div className='statement'>
                        <p className='emphasis'><Markdown>{testimonial.summary}</Markdown></p>
                        <p className='quote'>
                            <Markdown>{testimonial.testimonial}</Markdown>
                        </p>
                        <p className='testimonial-author'>
                            {testimonial.linkUrl && <a target='_blank' className='subtle-link' href={baseURL + testimonial.linkUrl} rel="noreferrer">{testimonial.fromName}</a>}
                            {!testimonial.linkUrl && testimonial.fromName}
                            {', ' + testimonial.fromLocation}
                        </p>
                    </div>
                </div>
                : null}
            <div className='funds-share-tools'>
                <div>
                    <h3>Share & help us grow support</h3>
                    <p>
                        Share this page with friends and family who want to make a difference.
                        If you&#39;re a teacher, <a href={baseURL + 'teachers'}>sign up</a> to get started!
                    </p>
                </div>
                <GenericShareBar
                    urlToShare={canonicalUrl}
                    trackedId={designatedFundId}
                    trackedPageType='designatedFund'
                    wrapperStyleClassName={'designated-fund-share-wrapper'}
                    emailSharePageType={'Challenge'}
                    emailShareId={challengeId}
                    supportForName={title}
                />
            </div>
            <div className='out-of-pocket'>
                <img src='/images/funds/coins.svg' alt=''/>
                <p><b>An average public school teacher will spend $624</b> out-of-pocket this year to keep their classroom running.</p>
            </div>
            <div className='price-comparison'>
                <p className='subheader'>
                    Here’s a look at what your generosity can bring to a classroom
                </p>
                <div className='price-explanations'>
                    <div className='explanation'>
                        <img src='/images/funds/price-tier-1.png?auto=webp' alt=''/>
                        <div className='price-description'>
                            <p className='subheader'>${alternateDonationTier1Amount}</p>
                            <h3 className='emphasis'>Stock the supply closet</h3>
                            <p className='info'>Fill a library with new books, stock a classroom with art supplies for a year, or provide essential tools like calculators for every student.</p>
                        </div>
                        <button className='button-primary' disabled={!acceptingDonations} onClick={() => submitDonation(alternateDonationTier1Amount)}>Give ${alternateDonationTier1Amount}</button>
                    </div>
                    <div className='explanation'>
                        <img src='/images/funds/price-tier-2.png?auto=webp' alt=''/>
                        <div className='price-description'>
                            <p className='subheader'>${alternateDonationTier2Amount}</p>
                            <h3 className='emphasis'>Spark new interests</h3>
                            <p className='info'>Provide flexible seating like wobble stools to help students focus, a robotics kit for hands-on coding, or a pair of tablets for digital learning.</p>
                        </div>
                        <button className='button-primary' disabled={!acceptingDonations} onClick={() => submitDonation(alternateDonationTier2Amount)}>Give ${alternateDonationTier2Amount}</button>
                    </div>
                    <div className='explanation'>
                        <img src='/images/funds/price-tier-3.png?auto=webp' alt=''/>
                        <div className='price-description'>
                            <p className='subheader'>${alternateDonationTier3Amount}</p>
                            <h3 className='emphasis'>Transform a classroom</h3>
                            <p className='info'>Fund a set of five Chromebooks, a full class set of ukuleles to start a music program, or advanced STEM equipment like microscopes.</p>
                        </div>
                        <button className='button-primary' disabled={!acceptingDonations} onClick={() => submitDonation(alternateDonationTier3Amount)}>Give ${alternateDonationTier3Amount}</button>
                    </div>
                </div>
            </div>
            <div className='dc-info'>
                <img src='/images/funds/trophy.svg' alt=''/>
                <h1>Most trusted by teachers</h1>
                <p className='subheader'>As a teacher-founded nonprofit, DonorsChoose is trusted by thousands of teachers and supporters across the country.</p>
                <div>
                    <a className='button-secondary' href={baseURL + 'about'}> Learn about DonorsChoose </a>
                    <p className='text-discreet'>If you&#39;re a teacher, <a href={baseURL + 'teachers'}>get funded!</a></p>
                </div>
            </div>
            <div className='learn-more-container'>
                <a className='learn-more-1 emphasis' href={baseURL + 'about'}>
                    <div>
                        <img src='/images/funds/learn-more-1.svg' alt=''/>
                        <span className='subtle-link'>Learn more</span>
                    </div>
                    <p className='emphasis'>DonorsChoose works with schools and districts directly.</p>
                </a>
                <a className='learn-more-2 emphasis' href={baseURL + 'about/finance.html'}>
                    <div>
                        <img src='/images/funds/learn-more-2.svg' alt=''/>
                        <span className='subtle-link'>Learn more</span>
                    </div>
                    <p>100% of your donation is tax-deductible!</p>
                </a>
            </div>
        </div>
    );
};

DesignatedFundLanding.propTypes = {
    canonicalUrl: PropTypes.string,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    challengeId: PropTypes.string.isRequired,
    designatedFundId: PropTypes.string.isRequired,
    photoUrl: PropTypes.string.isRequired,
    photoCaption: PropTypes.string,
    description: PropTypes.string.isRequired,
    activityFeedTitle: PropTypes.string,
    activityFeedDescription: PropTypes.string,
    activityFeed: PropTypes.object.isRequired,
    testimonial: PropTypes.object,
    donorCount: PropTypes.string,
    altImageText: PropTypes.string,
    totalRaised: PropTypes.number,
    currentGoal: PropTypes.number,
    acceptingDonations: PropTypes.bool.isRequired,
};

DesignatedFundLanding = React.memo(DesignatedFundLanding);
